import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styleSheet from '../styles/selectorFormation.module.scss';
import AddIcon from '../../assets/icons/AddIcon';

import { onErrorPlayerImage, getImageUrl } from '../../helpers/images';
import fallbackPlayer from '../../assets/graphics/fallback-player.svg';

const styles = styleSheet.locals || {};

const PLAYER_IMAGE_WIDTH = 84;

/**
 * TeamGrid component displays a grid of player positions that can be filled with players
 *
 * @param {object} props - Component props
 * @param {Array<{label: string, count: number}>} props.gridRows - Array of row configurations, each with a label and count of boxes
 * @param {{[key: string]: {display_name: string}}} props.boxPlayers - Object mapping box keys to player objects
 * @param {Function} props.onBoxClick - Callback function invoked when a box is clicked
 * @param props.editMode
 * @returns {React.ReactElement} The rendered TeamGrid component
 */
function TeamGrid({ gridRows, boxPlayers, onBoxClick, editMode = true }) {
    return (
        <div
            className={classNames(styles.selectorFormationTeamGrid, {
                [styles.selectorFormationTeamGridSaved]: !editMode
            })}
        >
            {gridRows.map((row, rowIndex) => {
                const totalBoxes = row.count;
                return (
                    <div
                        key={row.label}
                        className={styles.selectorFormationTeamGridRow}
                    >
                        {[...Array(totalBoxes)].map((unused, colIndex) => {
                            const boxKey = `${rowIndex}-${colIndex}`;
                            const playerObj = boxPlayers[boxKey];

                            const isBoxSelected = !!playerObj;

                            const isExternalImage =
                                playerObj?.external_image_url;

                            const imageUrl = isExternalImage
                                ? playerObj?.external_image_url
                                : getImageUrl(
                                      playerObj?.image_url,
                                      PLAYER_IMAGE_WIDTH
                                  );

                            return (
                                <div
                                    key={colIndex}
                                    className={classNames(
                                        styles.selectorFormationPlayerWrapper,
                                        {
                                            [styles.selectorFormationPlayerWrapperSelected]:
                                                isBoxSelected
                                        }
                                    )}
                                    onClick={() =>
                                        onBoxClick(rowIndex, colIndex)
                                    }
                                >
                                    {playerObj ? (
                                        <div
                                            className={
                                                styles.selectorFormationPlayer
                                            }
                                        >
                                            <img
                                                src={imageUrl || fallbackPlayer}
                                                alt={playerObj.display_name}
                                                className={
                                                    styles.selectorFormationPlayerImage
                                                }
                                                onError={onErrorPlayerImage}
                                            />

                                            <div
                                                className={
                                                    styles.selectorFormationPlayerName
                                                }
                                            >
                                                {playerObj.display_name}
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className={
                                                styles.selectorFormationAddPlayer
                                            }
                                        >
                                            <AddIcon />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

TeamGrid.propTypes = {
    gridRows: PropTypes.array.isRequired,
    boxPlayers: PropTypes.object.isRequired,
    onBoxClick: PropTypes.func.isRequired,
    editMode: PropTypes.bool
};

export default TeamGrid;
